/**
 * Anant Vyapaar Master — WhatsApp Marketing Suite
 * ------------------------------------------------
 * - 600 AT/mo · 6000 AT/yr subscription (3-level cascade: L1=15% L2=8% L3=5%)
 * - Ready templates + Custom message editor with voice input
 * - Customer groups + Bulk sequential wa.me sender (2s gap)
 * - QR shop-card generator (public /avm/:slug landing)
 * - AI rewrite (Claude via LLM gate)
 * - 0-cost tech stack — no paid WhatsApp API
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { fmtDateHuman, fmtDate, fmtDateTime } from '../../utils/dateFmt';
import { Mic, Copy, Trash2, Send, Plus, Sparkles, Users, QrCode, MessageSquare, Wand2, Upload, CheckCircle2, BarChart3, Shield, Edit3, Radio } from 'lucide-react';
import AIWalletGuard from '../../components/AIWalletGuard';
import ClientContactActions from '../../components/ClientContactActions';

const TAB_LIST = [
  { key: 'templates', label: 'Ready Templates', icon: MessageSquare },
  { key: 'custom',    label: 'Custom + AI',     icon: Wand2 },
  { key: 'customers', label: 'Customer Groups', icon: Users },
  { key: 'reminders', label: 'Birthday/Anniv',  icon: Sparkles },
  { key: 'upcoming',  label: 'Upcoming Events', icon: Sparkles },
  { key: 'schedule',  label: 'Scheduled',       icon: Send },
  { key: 'qr',        label: 'QR Shop Card',    icon: QrCode },
  { key: 'earnings',  label: 'Cashback',        icon: Sparkles },
  { key: 'history',   label: 'Broadcast History', icon: Radio },
  { key: 'plan',      label: 'Plan',            icon: Send },
];

function daysBetween(now, iso) {
  try { return Math.max(0, Math.ceil((new Date(iso) - now) / 86400000)); } catch { return 0; }
}

function waLink(mobile, text) {
  const digits = String(mobile || '').replace(/[^\d]/g, '');
  const encoded = encodeURIComponent(text || '');
  return `https://wa.me/${digits}?text=${encoded}`;
}

export default function AnantVyapaarMaster() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('templates');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/avm/config');
      setCfg(r.data);
    } catch (e) { toast.error('Failed to load AVM config'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !cfg) {
    return (<div className="min-h-screen bg-slate-950 text-white flex items-center justify-center pt-20" data-testid="avm-loading">
      <div className="animate-pulse text-emerald-300">Loading Anant Vyapaar Master...</div>
    </div>);
  }

  const active = cfg.user?.is_active;
  const expiry = cfg.user?.subscription?.expiry_at;
  const daysLeft = expiry ? daysBetween(new Date(), expiry) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-emerald-950/40 to-slate-950 text-white pt-20 pb-20" data-testid="avm-page">
      <div className="max-w-5xl mx-auto px-3">
        {/* Header */}
        <div className="rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-r from-emerald-700 via-teal-600 to-cyan-600 p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="text-4xl">📱</div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-black opacity-90">Anant Vyapaar Master</div>
              <div className="text-lg font-black leading-tight">WhatsApp Marketing · 3-Level Cashback · 0 Server Cost</div>
              <div className="text-[10px] opacity-80 mt-0.5">L1: 15% · L2: 8% · L3: 5% · Only active accounts earn cashback</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {active ? (
              <span className={`px-3 py-1 rounded-full text-xs font-black ${daysLeft <= 7 ? 'bg-amber-500/40 border-amber-300 animate-pulse' : daysLeft <= 30 ? 'bg-cyan-500/40 border-cyan-300' : 'bg-emerald-500/40 border-emerald-300'} border-2`}
                    data-testid="avm-plan-active">✅ ACTIVE · {daysLeft} days left</span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-black bg-red-500/40 border-2 border-red-300"
                    data-testid="avm-plan-inactive">🔒 Not subscribed</span>
            )}
            <span className="px-3 py-1 rounded-full text-xs font-black bg-white/15 border-2 border-white/25">
              💰 Balance: {cfg.user?.at_balance?.toFixed(0) || 0} AT
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-white/15 border-2 border-white/25">
              👥 Directs: {cfg.user?.directs_count || 0}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-2 mb-4 pb-2 -mx-3 px-3">
          {TAB_LIST.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`avm-tab-${t.key}`}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 border-2 transition ${tab === t.key ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-900 border-slate-700 text-slate-300'}`}>
                <Icon size={14} />{t.label}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        {tab === 'plan' && <PlanTab cfg={cfg} onReload={load} />}
        {tab === 'templates' && <TemplatesTab cfg={cfg} />}
        {tab === 'custom' && <CustomTab active={active} onSaved={load} cfg={cfg} />}
        {tab === 'customers' && <CustomersTab cfg={cfg} />}
        {tab === 'reminders' && <RemindersTab />}
        {tab === 'upcoming' && <UpcomingEventsTab />}
        {tab === 'schedule' && <ScheduleTab />}
        {tab === 'qr' && <QRTab active={active} />}
        {tab === 'earnings' && <EarningsTab cascade={cfg.cascade} />}
        {tab === 'history' && <BroadcastHistoryTab />}
      </div>
    </div>
  );
}

// ============ PLAN TAB ============
function PlanTab({ cfg, onReload }) {
  const [selected, setSelected] = useState('monthly');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const plans = cfg.plans || {};
  const active = cfg.user?.is_active;
  const daysLeft = cfg.user?.subscription?.expiry_at ? daysBetween(new Date(), cfg.user.subscription.expiry_at) : 0;
  const canRenew = !active || daysLeft <= 7;

  const subscribe = async () => {
    if (!canRenew) { toast.error(`Plan already active — ${daysLeft} days left`); return; }
    setBusy(true);
    try {
      const r = await api.post('/avm/subscribe', { plan: selected, referral_code: ref || null });
      toast.success(r.data?.message || 'Subscribed!');
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.values(plans).map((p) => (
          <button key={p.code} onClick={() => setSelected(p.code)} data-testid={`avm-plan-${p.code}`}
            className={`text-left p-4 rounded-2xl border-2 transition ${selected === p.code ? 'bg-emerald-600/20 border-emerald-400' : 'bg-slate-900 border-slate-700'}`}>
            <div className="text-xs uppercase font-black tracking-widest opacity-80">{p.label}</div>
            <div className="text-3xl font-black mt-1">{p.price_at} AT</div>
            <div className="text-xs opacity-70">{p.days} days</div>
            {p.code === 'yearly' && <div className="mt-2 text-[10px] px-2 py-1 rounded-full bg-amber-500/30 border border-amber-400/50 inline-block">🎁 Save 2 months</div>}
          </button>
        ))}
      </div>
      <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Referral code (optional)"
             data-testid="avm-referral-input"
             className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-sm" />
      <button onClick={subscribe} disabled={busy || !canRenew} data-testid="avm-subscribe-btn"
              className="w-full py-3 rounded-xl bg-emerald-500 text-slate-900 font-black disabled:opacity-50">
        {busy ? 'Processing...' : active && !canRenew ? `🔒 Active (${daysLeft} days left)` : active ? '🔄 Renew Now' : '🚀 Activate Plan'}
      </button>
      <div className="text-xs opacity-70 leading-relaxed p-3 rounded-xl bg-slate-900/60 border border-slate-800">
        <div className="font-black mb-1 text-emerald-300">🎯 3-Level Cascade Cashback (Active Accounts Only)</div>
        {(cfg.cascade || []).map((l) => (
          <div key={l.level}>Level {l.level}: <span className="text-emerald-300 font-black">{l.pct}% AT tokens</span></div>
        ))}
      </div>
    </div>
  );
}

// ============ READY TEMPLATES TAB ============
function TemplatesTab({ cfg }) {
  const [selected, setSelected] = useState(null);
  const [vars, setVars] = useState({ name: '', offer: '', phone: '', shop: '', amount: '', date: '', ref: '', city: '', address: '', product: '', price: '', review_link: '' });
  const [customerMobile, setCustomerMobile] = useState('');

  const templates = cfg.ready_templates || [];
  const categories = useMemo(() => Array.from(new Set(templates.map((t) => t.category))), [templates]);
  const [catFilter, setCatFilter] = useState(null);
  const filtered = catFilter ? templates.filter((t) => t.category === catFilter) : templates;

  const rendered = useMemo(() => {
    if (!selected) return '';
    let text = selected.text;
    Object.entries(vars).forEach(([k, v]) => { text = text.split(`{${k}}`).join(v || `{${k}}`); });
    return text;
  }, [selected, vars]);

  const send = () => {
    if (!customerMobile) { toast.error('Enter customer mobile'); return; }
    if (!rendered) { toast.error('Pick a template'); return; }
    window.open(waLink(customerMobile, rendered), '_blank');
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCatFilter(null)} className={`px-3 py-1.5 rounded-full text-xs font-black border-2 ${!catFilter ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>All</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCatFilter(c)} data-testid={`avm-cat-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-black border-2 whitespace-nowrap ${catFilter === c ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {filtered.map((t) => (
          <button key={t.id} onClick={() => setSelected(t)} data-testid={`avm-template-${t.id}`}
                  className={`p-3 rounded-xl border-2 text-left transition ${selected?.id === t.id ? 'bg-emerald-600/30 border-emerald-400' : 'bg-slate-900 border-slate-700'}`}>
            <div className="text-xs font-black">{t.label}</div>
            <div className="text-[10px] opacity-60 uppercase mt-0.5">{t.category}</div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="p-3 rounded-2xl bg-slate-900 border border-emerald-400/30 space-y-2">
          <div className="text-xs font-black text-emerald-300">Fill Variables</div>
          <div className="grid grid-cols-2 gap-2">
            {Array.from(selected.text.matchAll(/\{(\w+)\}/g)).map((m) => m[1]).filter((v, i, a) => a.indexOf(v) === i).map((k) => (
              <input key={k} placeholder={k} value={vars[k] || ''} onChange={(e) => setVars({ ...vars, [k]: e.target.value })}
                     data-testid={`avm-var-${k}`}
                     className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
            ))}
          </div>
          <div className="p-3 rounded-xl bg-slate-800 border border-slate-700 whitespace-pre-wrap text-sm" data-testid="avm-preview">
            {rendered}
          </div>
          <div className="flex gap-2">
            <input placeholder="Customer mobile" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)}
                   data-testid="avm-mobile-input"
                   className="flex-1 px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <button onClick={send} data-testid="avm-send-wa" className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 font-black text-sm">
              📤 Send WhatsApp
            </button>
            <button onClick={() => { navigator.clipboard.writeText(rendered); toast.success('Copied'); }} className="px-3 py-2 rounded-lg bg-slate-700 text-sm"><Copy size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CUSTOM MESSAGE + AI REWRITE ============
function CustomTab({ active, onSaved, cfg }) {
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [tone, setTone] = useState('friendly');
  const [templates, setTemplates] = useState([]);
  const recRef = useRef(null);
  const finalRef = useRef('');

  const load = async () => {
    try { const r = await api.get('/avm/templates'); setTemplates(r.data?.templates || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const startVoice = () => {
    const SR = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SR) { toast.error('Voice not supported'); return; }
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = 'hi-IN';
    finalRef.current = text || '';
    rec.onresult = (e) => {
      let fin = '', inter = '';
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) fin += t + ' '; else inter += t;
      }
      finalRef.current = fin.trim();
      setText((finalRef.current + ' ' + inter).replace(/\s+/g, ' ').trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start(); recRef.current = rec; setListening(true);
  };
  const stopVoice = () => { try { recRef.current?.stop(); } catch {} setListening(false); };

  const rewrite = async () => {
    if (!text || text.length < 5) { toast.error('Type message first'); return; }
    const bal = Number(cfg?.user?.at_balance || 0);
    if (bal < 20) { toast.error(`Sirf ${bal} AT — 20 chahiye. Top up karo!`); return; }
    const lowMsg = bal < 200
      ? `⚠️ Sirf ${bal} AT bacha hai. AI Rewrite pe 20 AT deduct hoga. Continue?`
      : 'AI Rewrite par 20 AT deduct hoga. Continue?';
    if (!window.confirm(lowMsg)) return;
    setAiBusy(true);
    try {
      const r = await api.post('/avm/ai-rewrite', { raw: text, tone });
      setText(r.data?.rewritten || text);
      toast.success('AI rewritten! 20 AT charged.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!label || !text) { toast.error('Label + text required'); return; }
    try {
      await api.post('/avm/templates', { label, text, category: 'custom' });
      toast.success('Saved!'); setLabel(''); setText(''); load();
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/avm/templates/${id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3">
      <AIWalletGuard atBalance={Number(cfg?.user?.at_balance || 0)} threshold={200} tool="AVM AI Rewrite" testId="avm-wallet-guard-banner" />
      <div className="p-3 rounded-2xl bg-slate-900 border border-emerald-400/30 space-y-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Template label (e.g. Diwali Sale)"
               data-testid="avm-custom-label"
               className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="relative">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                    placeholder="Apna message likhein ya bolein (mic dabaayein)..."
                    data-testid="avm-custom-text"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm resize-y" />
          <button onClick={listening ? stopVoice : startVoice} data-testid="avm-custom-mic"
                  className={`absolute right-2 top-2 p-2 rounded-full ${listening ? 'bg-red-500 animate-pulse' : 'bg-emerald-600'}`}>
            <Mic size={14} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="avm-ai-tone"
                  className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            <option value="friendly">Friendly</option>
            <option value="professional">Professional</option>
            <option value="festive">Festive</option>
            <option value="urgent">Urgent</option>
          </select>
          <button onClick={rewrite} disabled={aiBusy || !active} data-testid="avm-ai-rewrite"
                  className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black disabled:opacity-50 flex items-center gap-1">
            <Wand2 size={12} />{aiBusy ? 'Rewriting...' : 'AI Rewrite (20 AT)'}
          </button>
          <button onClick={save} data-testid="avm-custom-save"
                  className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">
            💾 Save
          </button>
        </div>
        {!active && <div className="text-[10px] text-amber-400">🔒 AI rewrite needs active plan</div>}
      </div>
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800" data-testid={`avm-custom-tpl-${t.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-emerald-300">{t.label}</div>
                <div className="text-xs opacity-80 whitespace-pre-wrap mt-1">{t.text.slice(0, 200)}{t.text.length > 200 ? '...' : ''}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { navigator.clipboard.writeText(t.text); toast.success('Copied'); }} className="p-1.5 rounded bg-slate-800"><Copy size={12} /></button>
                <button onClick={() => del(t.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {!templates.length && <div className="text-xs opacity-60 text-center py-6">Koi custom template nahin. Upar likhein ya bolein.</div>}
      </div>
    </div>
  );
}

// ============ CUSTOMER GROUPS + BULK SENDER ============
function CustomersTab({ cfg }) {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState({ group: null, delivered: null, source: null, birthday_month: null, anniversary_month: null, event_month: null, search: '' });
  const [form, setForm] = useState({ id: null, name: '', mobile: '', whatsapp: '', group: 'Regular', email: '', dob: '', anniversary: '', event_date: '', special_requirement: '', family_members: [] });
  const [showExtended, setShowExtended] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkMessage, setBulkMessage] = useState('');
  const [queueModal, setQueueModal] = useState(null);
  const [showCSV, setShowCSV] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [showCategoryMgr, setShowCategoryMgr] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceName, setVoiceName] = useState('');
  const [recording, setRecording] = useState(false);
  const [voiceDelaySec, setVoiceDelaySec] = useState(30);
  const [voiceLimit, setVoiceLimit] = useState({ max_daily: 30, used_24h: 0, remaining: 30, min_delay_sec: 20 });
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const voiceInputRef = useRef(null);
  useEffect(() => {
    api.get('/avm/broadcast/voice-limit').then((r) => setVoiceLimit(r.data)).catch(() => {});
  }, [queueModal?.voiceFile]);

  const startVoiceRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mime || 'audio/webm';
        const ext = type.includes('ogg') || type.includes('opus') ? 'opus' : (type.includes('mp4') ? 'm4a' : 'webm');
        const blob = new Blob(chunksRef.current, { type });
        blob.__ext = ext;
        setVoiceBlob(blob);
        setVoiceName(`avm-broadcast-${Date.now()}.${ext}`);
        toast.success('🎙 Voice-note recorded — select customers aur "Voice Broadcast" dabao');
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error('Mic access denied'); }
  };
  const stopVoiceRecord = () => { try { recorderRef.current?.stop(); } catch {} setRecording(false); };
  const pickVoiceFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setVoiceBlob(f);
    setVoiceName(f.name);
    toast.success(`🎙 ${f.name} loaded`);
    e.target.value = '';
  };
  const clearVoice = () => { setVoiceBlob(null); setVoiceName(''); };
  const startVoiceBulk = () => {
    if (!voiceBlob) { toast.error('Pehle voice record karo ya file upload karo'); return; }
    const targets = rows.filter((r) => selected.has(r.id));
    if (!targets.length) { toast.error('Select customers'); return; }
    if (voiceLimit.remaining <= 0) { toast.error(`Daily voice limit reached (${voiceLimit.used_24h}/${voiceLimit.max_daily}). 24 hr baad try karo.`); return; }
    if (targets.length > voiceLimit.remaining) {
      if (!window.confirm(`Sirf ${voiceLimit.remaining} bache hain (daily voice-limit ${voiceLimit.max_daily}). First ${voiceLimit.remaining} customers ko bhejein?`)) return;
    }
    const capped = targets.slice(0, voiceLimit.remaining);
    const delay = Math.max(voiceLimit.min_delay_sec || 20, Number(voiceDelaySec) || 30);
    const file = new File([voiceBlob], voiceName || `voice-${Date.now()}.opus`, { type: voiceBlob.type || 'audio/webm' });
    setQueueModal({
      items: capped.map((c) => ({ ...c, status: 'pending' })),
      message: bulkMessage || 'Namaste {name} ji, ek voice-note attach ki hai — sunein 🙏',
      idx: 0, autoAdvance: false, delaySec: delay,
      voiceFile: file,
    });
  };

  const load = async () => {
    try {
      const params = {};
      if (filter.group) params.group = filter.group;
      if (filter.delivered !== null) params.delivered = filter.delivered;
      if (filter.source) params.source = filter.source;
      if (filter.birthday_month) params.birthday_month = filter.birthday_month;
      if (filter.anniversary_month) params.anniversary_month = filter.anniversary_month;
      if (filter.event_month) params.event_month = filter.event_month;
      if (filter.search) params.search = filter.search;
      const r = await api.get('/avm/customers/filter', { params });
      setRows(r.data?.customers || []);
    } catch {}
  };
  const loadCategories = async () => {
    try {
      const r = await api.get('/avm/categories');
      setCategories(r.data?.categories || []);
      setGroups((r.data?.categories || []).map((c) => c.name));
    } catch {}
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { loadCategories(); }, []);

  const save = async () => {
    if (!form.name || !form.mobile) { toast.error('Name + mobile required'); return; }
    try {
      await api.post('/avm/customers', form);
      setForm({ id: null, name: '', mobile: '', whatsapp: '', group: 'Regular', email: '', dob: '', anniversary: '', event_date: '', special_requirement: '', family_members: [] });
      setShowExtended(false);
      load(); toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/avm/customers/${id}`); load(); };
  const markSent = async (id, delivered = true) => {
    try {
      await api.patch(`/avm/customers/${id}/mark-sent`, { delivered, channel: 'whatsapp' });
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  // CSV Export — download filtered rows
  const exportCSV = () => {
    if (!rows.length) { toast.error('No customers to export'); return; }
    const headers = ['name', 'mobile', 'whatsapp', 'group', 'email', 'dob', 'anniversary', 'event_date', 'special_requirement', 'source', 'delivered', 'created_at'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`📤 ${rows.length} customer(s) exported`);
  };

  // Voice-driven add — Hindi voice → parse "name X mobile Y"
  const [voiceRec, setVoiceRec] = useState(null);
  const startVoiceAdd = () => {
    const SR = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SR) { toast.error('Voice not supported'); return; }
    const r = new SR();
    r.continuous = false; r.interimResults = false; r.lang = 'hi-IN';
    r.onresult = (e) => {
      const txt = (e.results[0]?.[0]?.transcript || '').trim();
      if (!txt) return;
      // Extract 10-digit number
      const m = txt.match(/(\d[\d\s-]{8,}\d)/);
      const mobile = (m ? m[1] : '').replace(/[^\d]/g, '').slice(-10);
      // Name = txt with numbers stripped
      let name = txt.replace(/(\d[\d\s-]{8,}\d)/g, '').replace(/\b(mobile|number|nambar|naam|name|of|hai|is|ka|ki)\b/gi, '').trim();
      name = name.split(/\s+/).slice(0, 4).join(' ');
      setForm({ ...form, name: name || form.name, mobile: mobile || form.mobile });
      toast.success(`Heard: ${name} / ${mobile || '—'}`);
    };
    r.onend = () => setVoiceRec(null);
    r.onerror = () => setVoiceRec(null);
    r.start(); setVoiceRec(r);
  };

  const toggleSelect = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const selectAll = () => setSelected(new Set(rows.map((r) => r.id)));
  const clearAll = () => setSelected(new Set());

  const startBulk = () => {
    if (!bulkMessage) { toast.error('Type bulk message'); return; }
    const targets = rows.filter((r) => selected.has(r.id));
    if (!targets.length) { toast.error('Select customers'); return; }
    setQueueModal({
      items: targets.map((c) => ({ ...c, status: 'pending' })),
      message: bulkMessage, idx: 0, autoAdvance: false, delaySec: 60,
    });
  };

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="space-y-3">
      {/* WhatsApp Ban Safety Banner — always visible in customers tab */}
      <button onClick={() => setShowSafety(!showSafety)} data-testid="avm-safety-toggle"
              className="w-full p-3 rounded-2xl bg-gradient-to-r from-amber-950 to-red-950 border-2 border-amber-500/60 text-left flex items-center gap-2">
        <Shield className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-xs font-black text-amber-300">🛡️ WhatsApp Ban Safety Rules</div>
          <div className="text-[10px] opacity-70">Tap to see 8 rules · Your number ka ban prevention guarantee</div>
        </div>
        <span className="text-amber-400 text-xs">{showSafety ? '▲' : '▼'}</span>
      </button>
      {showSafety && <BanSafetyPanel />}

      {/* Add / Edit customer form */}
      <div className={`p-3 rounded-2xl bg-slate-900 border-2 ${form.id ? 'border-amber-400/70 ring-2 ring-amber-400/30' : 'border-emerald-400/30'} space-y-2`}>
        {form.id && (
          <div className="flex items-center justify-between text-[10px] text-amber-300 font-black" data-testid="avm-cust-editing-banner">
            ✏️ Editing: {form.name}
            <button onClick={() => { setForm({ id: null, name: '', mobile: '', whatsapp: '', group: 'Regular', email: '', dob: '', anniversary: '', event_date: '', special_requirement: '', family_members: [] }); setShowExtended(false); }}
                    data-testid="avm-cust-cancel-edit"
                    className="px-2 py-0.5 rounded bg-slate-700 text-white">✕ Cancel</button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="avm-cust-name" className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input placeholder="Mobile *" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                 data-testid="avm-cust-mobile" className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}
                  data-testid="avm-cust-group" className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button onClick={save} data-testid="avm-cust-save"
                  className="px-3 py-2 rounded-lg bg-emerald-500 text-slate-900 text-sm font-black flex items-center justify-center gap-1">
            <Plus size={14} /> {form.id ? 'Update' : 'Add'}
          </button>
        </div>
        <button onClick={() => setShowExtended(!showExtended)} data-testid="avm-cust-extended-toggle"
                className="text-[10px] text-emerald-300 underline">
          {showExtended ? '▲ Hide extended fields' : '▼ Show extended fields (WhatsApp / DOB / Anniversary / Event / Requirement)'}
        </button>
        {showExtended && (
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800">
            <input placeholder="WhatsApp (if different)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                   className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <div>
              <div className="text-[10px] opacity-70 mb-0.5">Birthday</div>
              <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
                     data-testid="avm-cust-dob" className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <div>
              <div className="text-[10px] opacity-70 mb-0.5">Anniversary</div>
              <input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })}
                     data-testid="avm-cust-anniv" className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <div>
              <div className="text-[10px] opacity-70 mb-0.5">Important Event</div>
              <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                     className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <textarea placeholder="Special requirement" value={form.special_requirement} onChange={(e) => setForm({ ...form, special_requirement: e.target.value })} rows={2}
                      className="col-span-2 px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm resize-y" />
            {/* Family Members (Wave 51) — reuses QR Family 360 save path */}
            <div className="col-span-2 rounded-lg bg-fuchsia-500/10 border border-fuchsia-400/40 p-2 space-y-1.5" data-testid="avm-cust-family">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-black text-fuchsia-200">👨‍👩‍👧 Family Members (DOB + Anniversary tracked)</div>
                <button type="button" onClick={() => setForm({ ...form, family_members: [...(form.family_members || []), { name: '', relation: '', dob: '', anniversary: '' }] })}
                        data-testid="avm-cust-family-add"
                        className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">+ Add member</button>
              </div>
              {(form.family_members || []).map((m, i) => (
                <div key={i} className="grid grid-cols-9 gap-1 items-center" data-testid={`avm-cust-family-row-${i}`}>
                  <input value={m.name} onChange={(e) => setForm({ ...form, family_members: form.family_members.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })}
                         placeholder="Name" className="col-span-2 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]" />
                  <input value={m.relation} onChange={(e) => setForm({ ...form, family_members: form.family_members.map((x, j) => j === i ? { ...x, relation: e.target.value } : x) })}
                         placeholder="Relation" className="col-span-2 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]" />
                  <input type="date" value={m.dob || ''} onChange={(e) => setForm({ ...form, family_members: form.family_members.map((x, j) => j === i ? { ...x, dob: e.target.value } : x) })}
                         title="Birthday" className="col-span-2 px-1 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
                  <input type="date" value={m.anniversary || ''} onChange={(e) => setForm({ ...form, family_members: form.family_members.map((x, j) => j === i ? { ...x, anniversary: e.target.value } : x) })}
                         title="Anniversary" className="col-span-2 px-1 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
                  <button type="button" onClick={() => setForm({ ...form, family_members: form.family_members.filter((_, j) => j !== i) })}
                          data-testid={`avm-cust-family-del-${i}`}
                          className="px-1.5 py-1 rounded bg-rose-600 text-white text-[10px] font-black">✕</button>
                </div>
              ))}
              {(form.family_members || []).length === 0 && (
                <div className="text-[10px] opacity-60">No family members yet. Add to get upcoming birthday / anniversary alerts.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick actions bar */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowCSV(true)} data-testid="avm-csv-open"
                className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-xs font-black flex items-center gap-1">
          <Upload size={12} /> CSV Import
        </button>
        <button onClick={exportCSV} data-testid="avm-csv-export"
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black flex items-center gap-1">
          📤 CSV Export
        </button>
        <button onClick={startVoiceAdd} data-testid="avm-voice-add"
                className={`px-3 py-1.5 rounded-lg text-white text-xs font-black flex items-center gap-1 ${voiceRec ? 'bg-red-500 animate-pulse' : 'bg-orange-600'}`}>
          <Mic size={12} /> {voiceRec ? 'Listening...' : 'Voice Add'}
        </button>
        <button onClick={() => setShowCategoryMgr(true)} data-testid="avm-cat-mgr"
                className="px-3 py-1.5 rounded-lg bg-fuchsia-600 text-white text-xs font-black">
          🏷️ Manage Categories
        </button>
      </div>

      {/* Filters */}
      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-800 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">🔍 Filters</div>
        <input placeholder="Search name / mobile / family-member name" value={filter.search}
               onChange={(e) => setFilter({ ...filter, search: e.target.value })}
               data-testid="avm-filter-search"
               className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
        <div className="flex gap-2 overflow-x-auto">
          <button onClick={() => setFilter({ ...filter, group: null })} className={`px-2 py-1 rounded-full text-[10px] font-black border-2 whitespace-nowrap ${!filter.group ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-800 border-slate-700'}`}>All</button>
          {groups.map((g) => (
            <button key={g} onClick={() => setFilter({ ...filter, group: g })} data-testid={`avm-filter-group-${g}`}
                    className={`px-2 py-1 rounded-full text-[10px] font-black border-2 whitespace-nowrap ${filter.group === g ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-800 border-slate-700'}`}>
              {g}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]">
          <select value={filter.delivered === null ? '' : String(filter.delivered)}
                  onChange={(e) => setFilter({ ...filter, delivered: e.target.value === '' ? null : e.target.value === 'true' })}
                  data-testid="avm-filter-delivery"
                  className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
            <option value="">Delivery: All</option>
            <option value="true">✓ Delivered</option>
            <option value="false">⏳ Pending</option>
          </select>
          <select value={filter.source || ''} onChange={(e) => setFilter({ ...filter, source: e.target.value || null })}
                  className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
            <option value="">Source: All</option>
            <option value="csv">CSV Import</option>
            <option value="qr_onboard">QR Onboard</option>
          </select>
          <select value={filter.birthday_month || ''} onChange={(e) => setFilter({ ...filter, birthday_month: e.target.value ? parseInt(e.target.value) : null })}
                  data-testid="avm-filter-birthday"
                  className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
            <option value="">🎂 Birthday: Any</option>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>🎂 {m}</option>)}
          </select>
          <select value={filter.anniversary_month || ''} onChange={(e) => setFilter({ ...filter, anniversary_month: e.target.value ? parseInt(e.target.value) : null })}
                  className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
            <option value="">💐 Anniv: Any</option>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>💐 {m}</option>)}
          </select>
        </div>
        <div className="text-[10px] opacity-70">Showing {rows.length} customer(s)</div>
      </div>

      {/* Bulk sender */}
      {rows.length > 0 && (
        <div className="p-3 rounded-2xl bg-slate-900 border border-purple-400/30 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-black text-purple-300">📢 Bulk Sequential Sender ({selected.size} selected)</div>
            <div className="flex gap-1">
              <button onClick={selectAll} className="text-[10px] px-2 py-1 rounded bg-slate-800">All</button>
              <button onClick={clearAll} className="text-[10px] px-2 py-1 rounded bg-slate-800">None</button>
            </div>
          </div>
          <textarea value={bulkMessage} onChange={(e) => setBulkMessage(e.target.value)} rows={3}
                    placeholder="Bulk message ({name} for personalisation). Tip: har baar thoda variation daalein — ban prevention ke liye zaroori."
                    data-testid="avm-bulk-message"
                    className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <button onClick={startBulk} disabled={!selected.size || !bulkMessage} data-testid="avm-bulk-send"
                  className="w-full py-2.5 rounded-lg bg-purple-500 text-white text-sm font-black disabled:opacity-50">
            🚀 Start Queue · {selected.size} customer(s)
          </button>

          {/* Voice broadcast block */}
          <div className="rounded-xl bg-slate-800/50 border border-rose-400/40 p-2 space-y-2" data-testid="avm-voice-block">
            <div className="flex items-center justify-between text-[10px]">
              <div className="font-black text-rose-300 uppercase flex items-center gap-1"><Radio size={11} /> Voice Broadcast</div>
              <span className={`px-1.5 py-0.5 rounded font-black ${voiceLimit.remaining > 5 ? 'bg-emerald-500/30 text-emerald-200' : voiceLimit.remaining > 0 ? 'bg-amber-500/30 text-amber-200' : 'bg-rose-500/30 text-rose-200'}`} data-testid="avm-voice-limit-badge">
                Daily: {voiceLimit.used_24h}/{voiceLimit.max_daily} · {voiceLimit.remaining} left
              </span>
            </div>
            {!voiceBlob ? (
              <div className="grid grid-cols-2 gap-2">
                {!recording ? (
                  <button onClick={startVoiceRecord} data-testid="avm-voice-record"
                          className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-black">
                    <Mic size={13} /> Record Voice
                  </button>
                ) : (
                  <button onClick={stopVoiceRecord} data-testid="avm-voice-stop"
                          className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-700 animate-pulse text-white text-xs font-black">
                    ⏹ Stop
                  </button>
                )}
                <button onClick={() => voiceInputRef.current?.click()} data-testid="avm-voice-upload"
                        className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black">
                  <Upload size={13} /> Upload MP3
                </button>
                <input ref={voiceInputRef} type="file" accept="audio/*,.mp3,.opus,.ogg,.wav,.m4a,.webm" onChange={pickVoiceFile} className="hidden" />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="rounded bg-slate-900/70 p-2 flex items-center gap-2" data-testid="avm-voice-preview">
                  <div className="text-[10px] text-emerald-300 font-black truncate flex-1">🎙 {voiceName}</div>
                  <button onClick={clearVoice} className="text-[10px] text-rose-300 font-black" data-testid="avm-voice-clear">✕ Remove</button>
                </div>
                <audio controls src={URL.createObjectURL(voiceBlob)} className="w-full h-8" />
                <label className="text-[10px] flex items-center gap-2" data-testid="avm-voice-delay-block">
                  <span className="opacity-80 font-black">⏱ Delay:</span>
                  <input type="range" min={voiceLimit.min_delay_sec || 20} max={180} value={voiceDelaySec}
                         onChange={(e) => setVoiceDelaySec(Number(e.target.value))}
                         data-testid="avm-voice-delay-slider"
                         className="flex-1" />
                  <span className="font-black text-rose-300">{voiceDelaySec}s</span>
                </label>
                <div className="text-[9px] opacity-60">Ban-safety: min {voiceLimit.min_delay_sec}s delay + max {voiceLimit.max_daily} voice/day</div>
                <button onClick={startVoiceBulk} disabled={!selected.size || voiceLimit.remaining <= 0} data-testid="avm-voice-broadcast-start"
                        className="w-full py-2 rounded-lg bg-rose-500 text-white text-sm font-black disabled:opacity-50">
                  📻 Broadcast Voice · {Math.min(selected.size, voiceLimit.remaining)} customer(s)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className={`p-3 rounded-xl bg-slate-900 border ${c.delivered ? 'border-emerald-500/40' : 'border-slate-800'} flex items-center gap-2`} data-testid={`avm-cust-row-${c.id}`}>
            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)}
                   data-testid={`avm-cust-check-${c.id}`}
                   className="w-4 h-4 accent-emerald-500" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black flex items-center gap-1">
                {c.name} {c.delivered && <span className="text-[10px] text-emerald-400">✓ Delivered</span>}
                {c.source === 'qr_onboard' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">🔗 QR</span>}
                {c.source === 'csv' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300">📥 CSV</span>}
              </div>
              <div className="text-[10px] opacity-70">{c.mobile} · <span className="text-emerald-300">{c.group}</span></div>
              {(c.dob || c.anniversary) && (
                <div className="text-[9px] opacity-60 mt-0.5 flex gap-2">
                  {c.dob && <span>🎂 {c.dob}</span>}
                  {c.anniversary && <span>💐 {c.anniversary}</span>}
                </div>
              )}
              {(c.family_members || []).length > 0 && (
                <div className="text-[9px] opacity-70 mt-0.5" data-testid={`avm-cust-family-chip-${c.id}`}>
                  👨‍👩‍👧 {c.family_members.length} family: {c.family_members.slice(0, 3).map((m) => `${m.name}${m.relation ? ' (' + m.relation + ')' : ''}`).join(', ')}
                </div>
              )}
            </div>
            <button onClick={() => markSent(c.id, !c.delivered)} data-testid={`avm-cust-delivered-${c.id}`}
                    title="Mark delivered"
                    className={`p-1.5 rounded ${c.delivered ? 'bg-emerald-600' : 'bg-slate-700'}`}>
              <CheckCircle2 size={12} />
            </button>
            <button onClick={() => {
                      setForm({
                        id: c.id, name: c.name || '', mobile: c.mobile || '',
                        whatsapp: c.whatsapp || '', group: c.group || 'Regular',
                        email: c.email || '', dob: c.dob || '', anniversary: c.anniversary || '',
                        event_date: c.event_date || '', special_requirement: c.special_requirement || '',
                        family_members: Array.isArray(c.family_members) ? c.family_members : [],
                      });
                      setShowExtended(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    data-testid={`avm-cust-edit-${c.id}`}
                    title="Edit customer"
                    className="p-1.5 rounded bg-amber-500 text-slate-900">
              <Edit3 size={12} />
            </button>
            <ClientContactActions phone={c.whatsapp || c.mobile} name={c.name} message={`Namaste ${c.name}`} size="xs" testId={`avm-cust-actions-${c.id}`} />
            <button onClick={() => del(c.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
          </div>
        ))}
        {!rows.length && <div className="text-xs opacity-60 text-center py-6">Koi customer nahin. Upar add karein, CSV upload karein, ya QR onboarding link share karein.</div>}
      </div>

      {queueModal && <BulkSendQueue modal={queueModal} setModal={setQueueModal} onDeliveredMark={markSent} />}
      {showCSV && <CSVImportModal onClose={() => { setShowCSV(false); load(); }} defaultGroup={form.group} />}
      {showCategoryMgr && <CategoryManagerModal categories={categories} onClose={() => { setShowCategoryMgr(false); loadCategories(); }} />}
    </div>
  );
}

// ============ BAN SAFETY PANEL ============
function BanSafetyPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/avm/ban-safety').then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-3 rounded-2xl bg-slate-900 text-xs opacity-70">Loading safety rules...</div>;
  return (
    <div className="p-3 rounded-2xl bg-slate-900 border border-amber-500/40 space-y-2" data-testid="avm-safety-panel">
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
          <div className="text-[9px] opacity-70">Today's Broadcasts</div>
          <div className="text-lg font-black text-emerald-300">{data.used_today} / {data.safety.max_daily_broadcasts_per_number}</div>
        </div>
        <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <div className="text-[9px] opacity-70">Safe Delay Between Sends</div>
          <div className="text-lg font-black text-amber-300">{data.safety.recommended_delay_sec}s</div>
        </div>
      </div>
      <ol className="text-[11px] space-y-1 opacity-90 list-decimal list-inside">
        {(data.safety.rules || []).map((r, i) => <li key={i}>{r}</li>)}
      </ol>
      <div className="text-[10px] text-red-300 font-black p-2 rounded bg-red-950/40 border border-red-500/30">
        ⚠️ Auto-Advance default 60s pe hai — kabhi bhi 30s ke neeche mat karein, ban trigger ho jaayega.
      </div>
    </div>
  );
}

// ============ CSV IMPORT MODAL ============
function CSVImportModal({ onClose, defaultGroup }) {
  const [text, setText] = useState('');
  const [group, setGroup] = useState(defaultGroup || 'Regular');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(f);
  };

  const doImport = async () => {
    if (!text.trim()) { toast.error('CSV empty'); return; }
    setBusy(true);
    try {
      const r = await api.post('/avm/customers/csv-import', { csv_text: text, default_group: group });
      setResult(r.data);
      toast.success(`✅ ${r.data.inserted} imported`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const sample = "name,mobile,whatsapp,group,dob,anniversary,event_date,special_requirement,family1_name,family1_relation,family1_dob,family1_anniversary,family2_name,family2_relation,family2_dob,family2_anniversary\nRahul Sharma,9876543210,,VIP,1990-05-12,2015-06-20,,Discount lover,Priya Sharma,spouse,1992-08-15,2015-06-20,Aarav Sharma,son,2018-03-22,\nPriya Verma,8765432109,,Regular,,,,,,,,,,,,";

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3" onClick={onClose} data-testid="avm-csv-modal">
      <div className="bg-slate-900 border-2 border-cyan-400 rounded-2xl w-full max-w-lg text-white overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 bg-gradient-to-r from-cyan-700 to-blue-700 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-black opacity-90">📥 CSV Import Customers</div>
            <div className="text-[10px] opacity-70">Header row: name, mobile, whatsapp, group, dob, anniversary, event_date, special_requirement, family1_name, family1_relation, family1_dob, family1_anniversary, …family2_, …family3_, …family4_</div>
          </div>
          <button onClick={onClose} className="text-2xl">✕</button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex gap-2 items-center">
            <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} data-testid="avm-csv-file"
                   className="text-xs flex-1" />
            <select value={group} onChange={(e) => setGroup(e.target.value)} className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
              <option value="Regular">Regular</option>
              <option value="VIP">VIP</option>
              <option value="Bakaya">Bakaya</option>
              <option value="Wholesale">Wholesale</option>
            </select>
          </div>
          <button onClick={() => setText(sample)} className="text-[10px] text-cyan-300 underline">Load sample CSV</button>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
                    placeholder="Paste CSV here or upload file above..."
                    data-testid="avm-csv-textarea"
                    className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs font-mono resize-y" />
          <button onClick={doImport} disabled={busy || !text.trim()} data-testid="avm-csv-import-btn"
                  className="w-full py-2.5 rounded-lg bg-cyan-500 text-slate-900 font-black text-sm disabled:opacity-50">
            {busy ? 'Importing...' : '📥 Import Customers'}
          </button>
          {result && (
            <div className="p-2 rounded-lg bg-emerald-900/30 border border-emerald-500/40 text-xs">
              ✅ Inserted: <b>{result.inserted}</b> · Skipped: {result.skipped} · Invalid: {result.invalid}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ CATEGORY MANAGER MODAL ============
function CategoryManagerModal({ categories, onClose }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/avm/categories', { name: name.trim() });
      setName(''); toast.success('Added');
      // Refresh list via parent onClose reload
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (nm) => {
    if (!window.confirm(`Delete category "${nm}"?`)) return;
    try { await api.delete(`/avm/categories/${encodeURIComponent(nm)}`); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3" onClick={onClose} data-testid="avm-cat-modal">
      <div className="bg-slate-900 border-2 border-fuchsia-400 rounded-2xl w-full max-w-md text-white overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 bg-gradient-to-r from-fuchsia-700 to-purple-700 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-black opacity-90">🏷️ Customer Categories</div>
            <div className="text-[10px] opacity-70">Add your own groups (Wholesale, Distributor, Family, etc.)</div>
          </div>
          <button onClick={onClose} className="text-2xl">✕</button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name"
                   data-testid="avm-cat-input"
                   className="flex-1 px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <button onClick={add} disabled={busy || !name.trim()} data-testid="avm-cat-add"
                    className="px-3 py-2 rounded-lg bg-fuchsia-500 text-white font-black text-sm disabled:opacity-50">Add</button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {(categories || []).map((c) => (
              <div key={c.name} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/60 border border-slate-700 text-xs">
                <div>
                  <b>{c.name}</b>
                  {c.default && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-700 opacity-70">default</span>}
                </div>
                {!c.default && <button onClick={() => del(c.name)} className="p-1 rounded bg-red-900/60"><Trash2 size={10} /></button>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ BULK SEND SEQUENTIAL QUEUE (popup-blocker safe) ============
// Each "Send Next" click = 1 user gesture = 1 wa.me popup allowed by browser.
// User sends the message in WhatsApp, returns to tab, clicks Send Next for the next customer.
function BulkSendQueue({ modal, setModal, onDeliveredMark }) {
  const { items, message, idx, autoAdvance, delaySec, voiceFile } = modal;
  const total = items.length;
  const done = items.filter((i) => i.status !== 'pending').length;
  const current = items[idx] || null;
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const personalize = (msg, cust) => {
    return (msg || '')
      .replace(/\{name\}/g, cust.name || '')
      .replace(/\{mobile\}/g, cust.mobile || '')
      .replace(/\{group\}/g, cust.group || '');
  };

  const sendCurrent = async () => {
    if (!current) return;
    const text = personalize(message, current);
    const phone = String(current.whatsapp || current.mobile || '').replace(/[^0-9]/g, '');

    // 3-step voice ladder — only when voiceFile is attached
    let sharedNatively = false;
    if (voiceFile && phone) {
      try {
        const payload = { title: 'Voice note', text, files: [voiceFile] };
        if (navigator.canShare && navigator.canShare(payload)) {
          await navigator.share(payload);
          sharedNatively = true;
        }
      } catch (e) { if (e && e.name === 'AbortError') { return; } }
    }
    if (!sharedNatively) {
      // Try 2/3: download the voice + open wa.me/PHONE with text
      if (voiceFile) {
        const a = document.createElement('a'); a.href = URL.createObjectURL(voiceFile);
        a.download = voiceFile.name; document.body.appendChild(a); a.click(); document.body.removeChild(a);
        toast.success('⬇️ Voice download · WA me 📎 se attach karein');
      }
      const url = waLink(current.whatsapp || current.mobile, text);
      window.open(url, '_blank');
    }
    if (onDeliveredMark) onDeliveredMark(current.id, true).catch(() => {});
    const next = items.map((it, i) => i === idx ? { ...it, status: 'sent', sent_at: new Date().toISOString() } : it);
    const nextIdx = findNextPending(next, idx);
    setModal({ ...modal, items: next, idx: nextIdx });
    setCountdown(0);
  };

  const skipCurrent = () => {
    const next = items.map((it, i) => i === idx ? { ...it, status: 'skipped' } : it);
    const nextIdx = findNextPending(next, idx);
    setModal({ ...modal, items: next, idx: nextIdx });
    setCountdown(0);
  };

  const copyOnly = () => {
    if (!current) return;
    const text = personalize(message, current);
    navigator.clipboard.writeText(text).then(() => toast.success('Copied — paste in WhatsApp'));
  };

  // Auto-advance timer — starts countdown after Send · Next
  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (!autoAdvance || !current || countdown <= 0) return;
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); timerRef.current = null; sendCurrent(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, autoAdvance, idx]);

  const armAutoAdvance = () => setCountdown(delaySec || 60);

  const closeModal = async () => {
    const sent = items.filter((i) => i.status === 'sent');
    if (sent.length > 0) {
      try {
        await api.post('/avm/broadcast/log', {
          template_label: voiceFile ? `Voice Broadcast${voiceFile.name ? ' · ' + voiceFile.name : ''}` : 'Bulk Custom Queue',
          customer_ids: sent.map((c) => c.id),
          message_preview: (message || '').slice(0, 200),
          channel: voiceFile ? 'voice' : 'text',
          voice_name: voiceFile ? voiceFile.name : null,
        });
      } catch { /* noop */ }
      toast.success(`✅ ${sent.length}/${total} messages sent`);
    }
    setModal(null);
  };

  const allDone = idx === -1 || done === total;

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-end sm:items-center justify-center p-3 overflow-y-auto" onClick={closeModal} data-testid="avm-bulk-queue-modal">
      <div className="bg-slate-900 border-2 border-purple-400 rounded-2xl w-full max-w-lg text-white overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 bg-gradient-to-r from-purple-700 to-fuchsia-700 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-black opacity-90">{voiceFile ? '🎙 Voice Broadcast' : 'Bulk Send Queue'}</div>
            <div className="text-lg font-black">{done} / {total} sent</div>
          </div>
          <button onClick={closeModal} data-testid="avm-bulk-close" className="text-2xl">✕</button>
        </div>

        <div className="h-2 bg-slate-800">
          <div className="h-2 bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all" style={{ width: `${(done / total) * 100}%` }} />
        </div>

        {/* Auto-Advance Settings Bar */}
        <div className="px-3 py-2 bg-amber-950/40 border-b border-amber-500/30 flex items-center gap-2 text-[10px]">
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!autoAdvance} onChange={(e) => setModal({ ...modal, autoAdvance: e.target.checked })}
                   data-testid="avm-bulk-auto-toggle" className="accent-amber-400" />
            <span className="font-black">🤖 Auto-Advance</span>
          </label>
          <select value={delaySec || 60} onChange={(e) => setModal({ ...modal, delaySec: parseInt(e.target.value) })}
                  data-testid="avm-bulk-delay" className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-[10px]">
            <option value={30}>30s (min)</option>
            <option value={45}>45s</option>
            <option value={60}>60s ✓ safe</option>
            <option value={90}>90s (safest)</option>
            <option value={120}>2min</option>
          </select>
          <span className="opacity-70 text-[9px]">🛡️ Ban-safe delay</span>
        </div>

        {!allDone && current && (
          <div className="p-4 space-y-3">
            <div className="p-3 rounded-xl bg-slate-800 border border-purple-400/30">
              <div className="text-[10px] uppercase tracking-widest font-black text-purple-300">Now Sending To</div>
              <div className="text-xl font-black mt-1">{current.name}</div>
              <div className="text-sm opacity-70">{current.mobile} · <span className="text-emerald-300">{current.group}</span></div>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700 max-h-32 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest font-black opacity-60 mb-1">Message Preview</div>
              <div className="text-xs whitespace-pre-wrap">{personalize(message, current)}</div>
            </div>
            {autoAdvance && countdown > 0 && (
              <div className="p-2 rounded-lg bg-amber-500/20 border border-amber-400 text-center">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Auto-Send in</div>
                <div className="text-3xl font-black text-amber-300">{countdown}s</div>
                <button onClick={() => setCountdown(0)} className="text-[9px] text-red-300 underline">Cancel countdown</button>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={copyOnly} data-testid="avm-bulk-copy"
                      className="py-2 rounded-lg bg-slate-700 text-xs font-black">📋 Copy</button>
              <button onClick={skipCurrent} data-testid="avm-bulk-skip"
                      className="py-2 rounded-lg bg-amber-600 text-xs font-black">⏭ Skip</button>
              <button onClick={autoAdvance ? (countdown > 0 ? sendCurrent : armAutoAdvance) : sendCurrent} data-testid="avm-bulk-send-next" autoFocus
                      className="py-2 rounded-lg bg-emerald-500 text-slate-900 text-sm font-black">
                {autoAdvance && countdown === 0 ? `🤖 Arm ${delaySec || 60}s` : '📤 Send · Next'}
              </button>
            </div>
            <div className="text-[10px] text-center opacity-60">
              {autoAdvance
                ? `Auto-Advance ON — WhatsApp khulega, aap message send karke wapas aayenge, ${delaySec || 60}s baad next auto-fire.`
                : 'Tap 📤 Send · Next → WhatsApp khulega → aap message send karke wapas aayein → next customer par jump.'}
            </div>
          </div>
        )}

        {allDone && (
          <div className="p-6 text-center space-y-3" data-testid="avm-bulk-done">
            <div className="text-5xl">🎉</div>
            <div className="text-lg font-black">Queue Complete!</div>
            <div className="text-xs opacity-70">{done} sent · {items.filter((i) => i.status === 'skipped').length} skipped</div>
            <button onClick={closeModal} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 text-sm font-black">Close</button>
          </div>
        )}

        <div className="max-h-48 overflow-y-auto border-t border-slate-800">
          {items.map((it, i) => (
            <button key={it.id} onClick={() => !allDone && setModal({ ...modal, idx: i })}
                    disabled={allDone}
                    data-testid={`avm-bulk-item-${it.id}`}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-800 last:border-0 ${
                      i === idx && !allDone ? 'bg-purple-900/40' :
                      it.status === 'sent' ? 'bg-emerald-900/20' :
                      it.status === 'skipped' ? 'bg-amber-900/20 opacity-60' :
                      'hover:bg-slate-800/60'
                    }`}>
              <div className="w-5 text-center">
                {it.status === 'sent' ? '✅' : it.status === 'skipped' ? '⏭' : i === idx ? '👉' : '⏳'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black truncate">{it.name}</div>
                <div className="text-[10px] opacity-60">{it.mobile}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function findNextPending(items, fromIdx) {
  // First scan forward, then wrap and scan from start
  for (let i = fromIdx + 1; i < items.length; i++) if (items[i].status === 'pending') return i;
  for (let i = 0; i <= fromIdx; i++) if (items[i].status === 'pending') return i;
  return -1;
}

// ============ QR SHOP CARD ============
function QRTab({ active }) {
  const [card, setCard] = useState(null);
  const [form, setForm] = useState({ shop_name: '', owner_name: '', whatsapp_number: '', intro_message: '', theme_color: '#10b981' });
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(14);

  const load = async () => {
    try { const r = await api.get('/avm/qr-card'); if (r.data?.card) { setCard(r.data.card); setForm({ ...r.data.card }); } } catch {}
  };
  const loadAnalytics = async () => {
    try { const r = await api.get(`/avm/qr-card/analytics?days=${analyticsDays}`); setAnalytics(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (card) loadAnalytics(); }, [card, analyticsDays]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post('/avm/qr-card', form);
      setCard(r.data?.card); toast.success('QR card published!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const publicUrl = card ? `${window.location.origin}/avm/${card.slug}` : '';
  const onboardUrl = card ? `${publicUrl}?onboard=1` : '';
  const qrImgUrl = card ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(publicUrl)}` : '';
  const onboardQrUrl = card ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(onboardUrl)}` : '';

  const maxCount = Math.max(1, ...(analytics?.daily || []).flatMap((d) => [d.views, d.clicks, d.onboards]));

  return (
    <div className="space-y-3">
      {!active && <div className="p-3 rounded-xl bg-amber-900/40 border border-amber-500/50 text-xs">🔒 QR shop-card publish karne ke liye active plan chahiye.</div>}
      <div className="p-3 rounded-2xl bg-slate-900 border border-emerald-400/30 grid grid-cols-1 gap-2">
        <input placeholder="Shop name" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
               data-testid="avm-qr-shop" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input placeholder="Owner name (optional)" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
               className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input placeholder="WhatsApp number (10 digit)" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
               data-testid="avm-qr-wa" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <textarea placeholder="Intro message (shown to customer on WhatsApp open)" value={form.intro_message} rows={2}
                  onChange={(e) => setForm({ ...form, intro_message: e.target.value })}
                  className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <button onClick={save} disabled={busy || !active} data-testid="avm-qr-save"
                className="py-2 rounded-lg bg-emerald-500 text-slate-900 font-black text-sm disabled:opacity-50">
          {busy ? 'Publishing...' : card ? 'Update Card' : 'Publish QR Card'}
        </button>
      </div>
      {card && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-slate-900 border-2 border-emerald-400/40 text-center space-y-2" data-testid="avm-qr-display">
              <div className="text-xs uppercase tracking-widest font-black text-emerald-300">📞 Shop QR (Direct WA)</div>
              <img src={qrImgUrl} alt="QR" className="w-full max-w-[240px] mx-auto bg-white p-2 rounded-xl" />
              <div className="text-[10px] opacity-70">Scan → WhatsApp direct khulega</div>
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-[10px] text-emerald-300 underline break-all">{publicUrl}</a>
            </div>
            <div className="p-3 rounded-2xl bg-slate-900 border-2 border-cyan-400/40 text-center space-y-2" data-testid="avm-onboard-qr-display">
              <div className="text-xs uppercase tracking-widest font-black text-cyan-300">📝 Client Onboarding QR</div>
              <img src={onboardQrUrl} alt="Onboard QR" className="w-full max-w-[240px] mx-auto bg-white p-2 rounded-xl" />
              <div className="text-[10px] opacity-70">Scan → Client fills details form</div>
              <a href={onboardUrl} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-300 underline break-all">{onboardUrl}</a>
            </div>
          </div>
          <div className="text-center text-xs opacity-70">Views: {card.views || 0} · Clicks: {card.clicks || 0}</div>

          {/* QR Analytics Graph */}
          <div className="p-3 rounded-2xl bg-slate-900 border border-cyan-500/30 space-y-2" data-testid="avm-qr-analytics">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-cyan-300" />
                <div className="text-xs font-black text-cyan-300">Daily Analytics</div>
              </div>
              <select value={analyticsDays} onChange={(e) => setAnalyticsDays(parseInt(e.target.value))}
                      data-testid="avm-qr-analytics-days"
                      className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            {analytics?.daily?.length > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-center">
                    <div className="text-[9px] opacity-70">Views (total)</div>
                    <div className="text-lg font-black text-emerald-300">{analytics.totals?.views || 0}</div>
                  </div>
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-center">
                    <div className="text-[9px] opacity-70">Clicks (WA)</div>
                    <div className="text-lg font-black text-amber-300">{analytics.totals?.clicks || 0}</div>
                  </div>
                  <div className="p-2 rounded bg-cyan-500/10 border border-cyan-500/30 text-center">
                    <div className="text-[9px] opacity-70">Onboards</div>
                    <div className="text-lg font-black text-cyan-300">{analytics.totals?.onboards || 0}</div>
                  </div>
                </div>
                <div className="flex items-end gap-0.5 h-32 pt-2">
                  {analytics.daily.map((d, i) => {
                    const vH = (d.views / maxCount) * 100;
                    const cH = (d.clicks / maxCount) * 100;
                    const oH = (d.onboards / maxCount) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group" title={`${d.date}: ${d.views}v ${d.clicks}c ${d.onboards}o`}>
                        <div className="w-full flex items-end gap-0.5 h-full">
                          <div className="flex-1 bg-emerald-500 rounded-t" style={{ height: `${vH}%`, minHeight: d.views ? '2px' : '0' }} />
                          <div className="flex-1 bg-amber-500 rounded-t" style={{ height: `${cH}%`, minHeight: d.clicks ? '2px' : '0' }} />
                          <div className="flex-1 bg-cyan-500 rounded-t" style={{ height: `${oH}%`, minHeight: d.onboards ? '2px' : '0' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 text-[9px] opacity-70 justify-center">
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded" /> Views</span>
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500 rounded" /> WA Clicks</span>
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-cyan-500 rounded" /> Onboards</span>
                </div>
                <div className="text-[9px] opacity-60 text-center">{analytics.daily[0]?.date} → {analytics.daily[analytics.daily.length - 1]?.date}</div>
              </>
            ) : (
              <div className="text-xs opacity-60 text-center py-4">No traffic yet. Share your QR card to see stats.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============ EARNINGS ============
// ============ BROADCAST HISTORY TAB ============
function BroadcastHistoryTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState(''); // '' | 'voice' | 'text'
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { channel: filter } : {};
      const r = await api.get('/avm/broadcast/history', { params });
      setRows(r.data.broadcasts || []);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const resend = async (b) => {
    if (!b.unresponsive_ids?.length) { toast.error('Sabhi customers responsive hain'); return; }
    try {
      const r = await api.get(`/avm/broadcast/${b.id}/customers?unresponsive_only=true`);
      const custs = r.data.customers || [];
      if (custs.length === 0) { toast.error('No unresponsive customers'); return; }
      if (!window.confirm(`${custs.length} unresponsive customers ko re-send WA khulega. Continue?`)) return;
      const text = b.message_preview || 'Namaste {name}, previous message ka reply nahi mila — please reply karein 🙏';
      let idx = 0;
      const sendNext = () => {
        if (idx >= custs.length) { toast.success(`✅ ${custs.length} unresponsive WA opened`); return; }
        const c = custs[idx++];
        const phone = String(c.whatsapp || c.mobile || '').replace(/[^0-9]/g, '');
        const msg = text.replace(/\{name\}/g, c.name || '');
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
        setTimeout(sendNext, 800);
      };
      sendNext();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="avm-history-tab">
      <div className="flex items-center gap-2">
        <div className="text-xs font-black text-emerald-300">📻 Broadcast History</div>
        <div className="flex gap-1 ml-auto">
          {['', 'voice', 'text'].map((f) => (
            <button key={f || 'all'} onClick={() => setFilter(f)} data-testid={`avm-history-filter-${f || 'all'}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-black ${filter === f ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              {f ? (f === 'voice' ? '🎙 Voice' : '💬 Text') : 'All'}
            </button>
          ))}
          <button onClick={load} data-testid="avm-history-refresh" className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] font-black">🔄</button>
        </div>
      </div>
      {loading && <div className="text-center opacity-60 py-8">⏳ Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-center opacity-60 py-10 text-xs">Abhi tak koi broadcast nahi.</div>}
      <div className="space-y-2">
        {rows.map((b) => {
          const isVoice = b.channel === 'voice';
          const delivered = (b.count || 0) - (b.unresponsive_count || 0);
          return (
            <div key={b.id} className={`p-3 rounded-xl border ${isVoice ? 'bg-rose-500/10 border-rose-400/40' : 'bg-slate-900 border-slate-700'}`} data-testid={`avm-history-${b.id}`}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black truncate">
                    {isVoice ? '🎙' : '💬'} {b.template_label || (isVoice ? 'Voice Broadcast' : 'Bulk Broadcast')}
                    {isVoice && b.voice_name && <span className="text-[10px] opacity-70 ml-1">· {b.voice_name}</span>}
                  </div>
                  {b.message_preview && <div className="text-[10px] opacity-80 line-clamp-2 mt-0.5">{b.message_preview}</div>}
                  <div className="text-[9px] opacity-60 mt-1">
                    {b.created_at?.slice(0, 16).replace('T', ' ')} · Total: {b.count} · Delivered: {delivered} · Unresponsive: <b className="text-rose-300">{b.unresponsive_count || 0}</b>
                  </div>
                </div>
              </div>
              {(b.unresponsive_count || 0) > 0 && (
                <button onClick={() => resend(b)} data-testid={`avm-history-resend-${b.id}`}
                        className="mt-2 w-full py-1.5 rounded-lg bg-amber-500 text-slate-900 text-xs font-black">
                  🔁 Resend WA to {b.unresponsive_count} unresponsive
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



function EarningsTab({ cascade }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/avm/earnings?days=30').then((r) => setData(r.data)).catch(() => setData({ total_at: 0, transactions: [] }));
  }, []);
  if (!data) return <div className="text-center py-6 opacity-60">Loading...</div>;
  return (
    <div className="space-y-3">
      <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-700 to-cyan-700">
        <div className="text-xs uppercase tracking-widest font-black opacity-90">Total Cashback (30 days)</div>
        <div className="text-4xl font-black mt-1">{data.total_at || 0} AT</div>
        <div className="text-xs opacity-80 mt-1">{data.count || 0} payouts · {data.active_directs || 0}/{data.directs || 0} active directs</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {(cascade || []).map((l) => (
          <div key={l.level} className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-center">
            <div className="text-[10px] opacity-70">Level {l.level}</div>
            <div className="text-lg font-black text-emerald-300">{data.level_totals?.[`L${l.level}`] || 0}</div>
            <div className="text-[10px] opacity-60">AT · {l.pct}%</div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="text-xs font-black opacity-70">Recent Payouts</div>
        {(data.transactions || []).map((t, i) => (
          <div key={i} className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 text-xs flex justify-between">
            <div>
              <div className="font-black">{t.description}</div>
              <div className="opacity-60">{fmtDateHuman(t.created_at)}</div>
            </div>
            <div className="text-emerald-300 font-black">+{t.amount} AT</div>
          </div>
        ))}
        {!(data.transactions || []).length && <div className="text-xs opacity-60 text-center py-4">Koi cashback abhi tak nahin.</div>}
      </div>
    </div>
  );
}

// ============ BIRTHDAY / ANNIVERSARY REMINDERS TAB ============
// ============ UPCOMING FAMILY EVENTS TAB (Wave 51) ============
function UpcomingEventsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (month) params.month = parseInt(month, 10); else params.days = days;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/avm/family-events', { params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [days, month, search]);
  useEffect(() => { load(); }, [load]);

  const sendWish = (ev) => {
    const t = (data?.templates || {})[ev.is_family ? `family_${ev.event_kind}` : ev.event_kind] || '';
    const msg = t
      .replace(/\{customer_name\}/g, ev.customer_name || '')
      .replace(/\{name\}/g, ev.person_name || '')
      .replace(/\{family_member_name\}/g, ev.person_name || '')
      .replace(/\{relation\}/g, ev.relation || 'family member');
    const url = waLink(ev.customer_whatsapp || ev.customer_mobile, msg);
    window.open(url, '_blank');
    toast.success('WhatsApp opened');
  };

  const months = [
    { v: '', l: 'All (next ' + days + 'd)' },
    { v: '1', l: 'January' }, { v: '2', l: 'February' }, { v: '3', l: 'March' },
    { v: '4', l: 'April' }, { v: '5', l: 'May' }, { v: '6', l: 'June' },
    { v: '7', l: 'July' }, { v: '8', l: 'August' }, { v: '9', l: 'September' },
    { v: '10', l: 'October' }, { v: '11', l: 'November' }, { v: '12', l: 'December' },
  ];

  return (
    <div className="space-y-3" data-testid="avm-upcoming-tab">
      <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-700 to-fuchsia-700 text-center">
        <div className="text-xs uppercase tracking-widest opacity-90">Upcoming Family Events</div>
        <div className="text-4xl font-black mt-1" data-testid="avm-upcoming-count">{data?.total || 0}</div>
        <div className="text-xs opacity-80">
          {month ? `in ${months.find((x) => x.v === String(month))?.l}` : `in next ${days} days`}
          {search ? ` · matching "${search}"` : ''}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Search name / relation / phone"
               data-testid="avm-upcoming-search"
               className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <select value={month} onChange={(e) => setMonth(e.target.value)}
                data-testid="avm-upcoming-month"
                className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
          {months.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                disabled={!!month} data-testid="avm-upcoming-days"
                className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm disabled:opacity-40">
          {[7, 15, 30, 60, 90].map((d) => <option key={d} value={d}>Next {d} days</option>)}
        </select>
      </div>

      {loading && <div className="text-xs opacity-60 text-center py-4">Loading…</div>}
      {!loading && (data?.events || []).length === 0 && (
        <div className="text-xs opacity-60 text-center py-8" data-testid="avm-upcoming-empty">
          Koi upcoming event nahi mila. Filter badlo ya aur family members add karo.
        </div>
      )}

      <div className="space-y-2">
        {(data?.events || []).map((ev, i) => (
          <div key={i} className="p-3 rounded-xl bg-slate-900 border border-fuchsia-500/30 flex items-center gap-2"
               data-testid={`avm-upcoming-row-${i}`}>
            <div className="text-2xl">{ev.event_kind === 'birthday' ? '🎂' : '💐'}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black truncate">
                {ev.person_name}
                {ev.relation && <span className="ml-1 text-[11px] opacity-70">({ev.relation})</span>}
              </div>
              <div className="text-[10px] opacity-70 flex flex-wrap gap-1 items-center">
                {ev.is_family && <span className="px-1.5 py-0.5 rounded bg-fuchsia-500/25 text-fuchsia-200 font-black">👨‍👩‍👧 Family</span>}
                <span>via {ev.customer_name}</span>
                <span>· {ev.customer_mobile}</span>
                <span>· {ev.date}</span>
                {ev.days_until === 0 ? <span className="text-emerald-300 font-black">· TODAY</span>
                 : <span className="text-amber-300">· in {ev.days_until}d</span>}
              </div>
            </div>
            <button onClick={() => sendWish(ev)} data-testid={`avm-upcoming-wish-${i}`}
                    className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">📤 Wish</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ BIRTHDAY / ANNIVERSARY TAB ============
function RemindersTab() {
  const [data, setData] = useState(null);
  const [queueTarget, setQueueTarget] = useState(null); // { customer, template }

  const load = async () => { try { const r = await api.get('/avm/reminders/today'); setData(r.data); } catch {} };
  useEffect(() => { load(); }, []);

  const sendReminder = (cust, kind) => {
    if (!data) return;
    // If this reminder is for a family member, switch to family-flavour template
    const isFamily = !!cust._family_wish;
    let key = kind;
    if (isFamily && kind === 'birthday') key = 'family_birthday';
    if (isFamily && kind === 'anniversary') key = 'family_anniversary';
    const tpl = data.templates[key] || data.templates[kind] || '';
    const msg = tpl
      .replace(/\{name\}/g, cust.name || '')
      .replace(/\{family_member_name\}/g, cust.family_member_name || '')
      .replace(/\{family_member_relation\}/g, cust.family_member_relation || 'family member');
    const url = waLink(cust.whatsapp || cust.mobile, msg);
    window.open(url, '_blank');
    try { api.patch(`/avm/customers/${cust.id}/mark-sent`, { delivered: true, channel: 'whatsapp' }); } catch { /* noop */ }
    toast.success(isFamily ? 'Family wish WA opened' : 'WhatsApp opened');
  };

  if (!data) return <div className="text-xs opacity-60 text-center py-4">Loading...</div>;
  const sections = [
    { key: 'birthdays',     label: 'Birthdays Today',     kind: 'birthday',    emoji: '🎂', rows: data.birthdays },
    { key: 'anniversaries', label: 'Anniversaries Today', kind: 'anniversary', emoji: '💐', rows: data.anniversaries },
    { key: 'events',        label: 'Important Events Today', kind: 'event',    emoji: '📅', rows: data.events },
  ];

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-2xl bg-gradient-to-r from-pink-700 to-purple-700 text-center">
        <div className="text-xs uppercase tracking-widest opacity-90">Auto-Reminders · {data.date}</div>
        <div className="text-4xl font-black mt-1">{data.total}</div>
        <div className="text-xs opacity-80">customers to wish today</div>
      </div>
      {data.total === 0 && <div className="text-xs opacity-60 text-center py-8">Aaj koi birthday/anniversary/event nahin hai. Kal check karein!</div>}
      {sections.map((s) => s.rows.length > 0 && (
        <div key={s.key} className="space-y-2">
          <div className="text-xs font-black text-pink-300 flex items-center gap-1">{s.emoji} {s.label} ({s.rows.length})</div>
          {s.rows.map((c) => (
            <div key={(c.id || '') + (c.family_member_name || '')} className="p-3 rounded-xl bg-slate-900 border border-pink-500/30 flex items-center gap-2" data-testid={`avm-reminder-${c.id}${c._family_wish ? '-fam-' + (c.family_member_name || '').slice(0,10) : ''}`}>
              <div className="text-2xl">{s.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black">
                  {c._family_wish ? `${c.family_member_name} (${c.family_member_relation})` : c.name}
                </div>
                <div className="text-[10px] opacity-70">
                  {c._family_wish && <span className="mr-1 px-1.5 py-0.5 rounded bg-fuchsia-500/25 text-fuchsia-200 font-black">👨‍👩‍👧 Family</span>}
                  {c.mobile} · {c.group}{c._family_wish ? ` · via ${c.name}` : ''}
                </div>
              </div>
              <button onClick={() => sendReminder(c, s.kind)} data-testid={`avm-reminder-send-${c.id}${c._family_wish ? '-fam' : ''}`}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">📤 Wish</button>
            </div>
          ))}
        </div>
      ))}
      <div className="text-[10px] text-center opacity-60 p-2 rounded bg-slate-900/60">
        💡 Tool har roz aapke customer's birthday/anniversary/event detect karta hai. Ek click me personal wish + special offer bhejein.
      </div>
    </div>
  );
}

// ============ SCHEDULED BROADCASTS TAB (localStorage + browser Notification API) ============
function ScheduleTab() {
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState({ label: '', message: 'Namaste {name} ji, aapka bakaya {amount} pending hai. Kripya jaldi settle karein. 🙏', group: 'Bakaya', fire_at: '' });
  const [notifyPerm, setNotifyPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');

  const load = async () => { try { const r = await api.get('/avm/schedule'); setSchedules(r.data?.schedules || []); } catch {} };
  useEffect(() => { load(); }, []);

  // Poll every 30s — fire notification if fire_at has passed
  useEffect(() => {
    const iv = setInterval(async () => {
      const now = new Date();
      for (const s of schedules) {
        if (s.fired) continue;
        const at = new Date(s.fire_at);
        if (at <= now) {
          // Fire browser notification
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              new Notification('📢 ' + s.label, { body: s.message.slice(0, 100), icon: '/favicon.ico', tag: s.id });
            } catch { /* noop */ }
          }
          try { await api.delete(`/avm/schedule/${s.id}`); } catch {}
          toast(`📢 Time hai! ${s.label} broadcast karein`, { icon: '⏰', duration: 8000 });
          load();
        }
      }
    }, 30000);
    return () => clearInterval(iv);
  }, [schedules]);

  const requestNotify = async () => {
    if (typeof Notification === 'undefined') { toast.error('Notifications not supported'); return; }
    const p = await Notification.requestPermission();
    setNotifyPerm(p);
    if (p === 'granted') toast.success('✅ Notifications enabled');
  };

  const save = async () => {
    if (!form.label || !form.message || !form.fire_at) { toast.error('Sab fields fill karein'); return; }
    try {
      await api.post('/avm/schedule', form);
      toast.success('⏰ Scheduled'); load();
      setForm({ ...form, label: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Cancel schedule?')) return; await api.delete(`/avm/schedule/${id}`); load(); };

  const quickBakayaTomorrow = () => {
    const t = new Date(); t.setDate(t.getDate() + 1); t.setHours(10, 0, 0, 0);
    setForm({
      label: 'Bakaya Reminder — ' + fmtDate(t),
      message: 'Namaste {name} ji, aapka pending payment yaad dilana hai. Kripya aaj settle karein. 🙏',
      group: 'Bakaya',
      fire_at: t.toISOString().slice(0, 16),
    });
    toast.success('Bakaya template loaded — kal 10 AM');
  };

  return (
    <div className="space-y-3">
      {notifyPerm !== 'granted' && (
        <button onClick={requestNotify} data-testid="avm-notify-enable"
                className="w-full p-3 rounded-xl bg-cyan-900/40 border-2 border-cyan-500/50 text-cyan-200 text-sm font-black">
          🔔 Enable Browser Notifications (schedule fire ke liye zaroori)
        </button>
      )}
      <button onClick={quickBakayaTomorrow} data-testid="avm-quick-bakaya"
              className="w-full p-3 rounded-xl bg-gradient-to-r from-amber-600 to-red-600 text-white font-black text-sm">
        ⚡ Quick: Bakaya Reminder Kal 10 AM
      </button>
      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2">
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Broadcast label (e.g. Diwali offer)"
               data-testid="avm-sched-label" className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3}
                  placeholder="Message ({name} for personalisation)"
                  data-testid="avm-sched-message"
                  className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="Group (Bakaya/VIP)"
                 className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input type="datetime-local" value={form.fire_at} onChange={(e) => setForm({ ...form, fire_at: e.target.value })}
                 data-testid="avm-sched-time"
                 className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        </div>
        <button onClick={save} data-testid="avm-sched-save"
                className="w-full py-2 rounded-lg bg-orange-500 text-slate-900 font-black text-sm">⏰ Schedule Broadcast</button>
      </div>
      <div className="space-y-2">
        <div className="text-xs font-black opacity-70">📋 Upcoming Schedules ({schedules.length})</div>
        {schedules.map((s) => {
          const t = new Date(s.fire_at);
          const remaining = Math.max(0, Math.ceil((t - new Date()) / 60000));
          return (
            <div key={s.id} className="p-3 rounded-xl bg-slate-900 border border-orange-500/30 flex items-center gap-2">
              <div className="text-2xl">⏰</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black">{s.label}</div>
                <div className="text-[10px] opacity-70">{fmtDateTime(t)} · Group: {s.group || 'All'} · in {remaining < 60 ? remaining + 'm' : Math.ceil(remaining / 60) + 'h'}</div>
              </div>
              <button onClick={() => del(s.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
            </div>
          );
        })}
        {schedules.length === 0 && <div className="text-xs opacity-60 text-center py-4">Koi scheduled broadcast nahin.</div>}
      </div>
      <div className="text-[10px] text-center opacity-60 p-2 rounded bg-slate-900/60">
        💡 Schedule set karne ke baad tab open rakho ya wapas aayo — jab time aayega, browser notification aayega. Fir aap Customers tab me jaake bulk queue start kar sakte ho.
      </div>
    </div>
  );
}
